import { useRef, useState, useEffect } from "react";
import { Tooltip } from "antd";
import { useMobileViewport } from "hooks/useMobileViewport";

/**
 * Walks down the parent DOM node's first child element chain to define overflow status
 * This handles nested structures like Antd Button where the overflow is only correctly detectable
 * by finding and comparing against the real element containing the text
 */
function computeOverflowStatus(element) {
  if (!element) return false;

  let currentElement = element;
  while (currentElement) {
    if (currentElement.scrollWidth > currentElement.clientWidth) return true;
    currentElement = currentElement.firstElementChild;
  }

  return false;
};

const DynamicTooltip = ({ title, className, onClick, children, ...tooltipProps }) => {
  const { isMobileWidth } = useMobileViewport();
  const parentContainerRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const parentContainer = parentContainerRef.current;
    if (!parentContainer) return;

    const updateOverflowStatus = () => setIsOverflowing(computeOverflowStatus(parentContainer));
    updateOverflowStatus();
    
    const ro = new ResizeObserver(updateOverflowStatus);
    ro.observe(parentContainer);
    return () => ro.disconnect();
  }, [isMobileWidth]);

  useEffect(() => {
    if (parentContainerRef.current) setIsOverflowing(computeOverflowStatus(parentContainerRef.current));
  }, [title, isMobileWidth]);

  if (isMobileWidth) {
    return (
      <div className={className} onClick={onClick} ref={parentContainerRef}>
        {children}
      </div>
    );
  }

  return (
    <Tooltip title={isOverflowing ? title : false} destroyTooltipOnHide {...tooltipProps}>
      <div className={className} onClick={onClick} ref={parentContainerRef}>
        {children}
      </div>
    </Tooltip>
  );
};

export default DynamicTooltip;